import React, { useRef, useState, useEffect } from 'react'
import type { Theme } from '@material-ui/core'
import { makeStyles, Popover } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import { SketchPicker } from 'react-color'
import type { InputProps as MuiInputProps } from '@material-ui/core/Input'
import classNames from 'classnames'
import type { StyleClasses } from '../../styles'
import { InputField } from '../../'
import type { CoreColorPickerProps } from './index'

const StyledSketchPicker = SketchPicker as React.ComponentType<
  React.ComponentProps<typeof SketchPicker> & { styles?: any }
>

const SELECTION_STYLE_ID = 'ps-inline-color-picker-hide-selection'

const useStyles = makeStyles<Theme, InlineColorPickerProps>(
  (theme) => ({
    root: {},
    input: {},
    popover: {
      position: 'absolute',
      zIndex: 2
    },
    swatch: {
      height: 28,
      width: 28,
      '&:hover': {
        cursor: 'pointer'
      }
    },
    inlineSwatch: {
      width: 28,
      height: 28,
      cursor: 'pointer',
      borderWidth: 1,
      borderStyle: 'solid'
    },
    popoverPaper: {
      marginTop: theme.typography.pxToRem(8),
      borderRadius: theme.typography.pxToRem(12),
      overflow: 'hidden',
      border: `1px solid ${theme.palette.divider}`,
      background: theme.palette.common.white,
      boxShadow: theme.shadows[2]
    },
    popoverContent: {
      display: 'flex',
      flexDirection: 'column',
      padding: 0,
      touchAction: 'none',
      background: theme.palette.common.white,
      '& .sketch-picker': {
        borderRadius: theme.typography.pxToRem(12),
        overflow: 'hidden',
        border: 'none'
      },
      '& .sketch-picker .flexbox-fix input': {
        borderRadius: theme.typography.pxToRem(6),
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
        background: theme.palette.common.white,
        padding: `${theme.typography.pxToRem(4)} ${theme.typography.pxToRem(12)}`,
        textAlign: 'center'
      },
      '& .sketch-picker .flexbox-fix .hue-horizontal > div > div': {
        width: `${theme.typography.pxToRem(3)} !important`,
        height: `${theme.typography.pxToRem(8)} !important`,
        borderRadius: `${theme.typography.pxToRem(1.5)} !important`,
        boxShadow: 'none !important',
        background: '#fff !important',
        transform: 'translateX(-3px) !important'
      },
      '& .sketch-picker .flexbox-fix .hue': {}
    },
    clearFooter: {
      borderTop: 'none',
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      paddingTop: theme.spacing(0.5),
      display: 'flex',
      justifyContent: 'space-between',
      background: theme.palette.common.white
    },
    clearButton: {
      background: 'none',
      border: 'none',
      padding: 0,
      marginLeft: 0,
      color: theme.palette.text.primary,
      cursor: 'pointer',
      fontWeight: theme.typography.fontWeightRegular,
      letterSpacing: '0',
      textTransform: 'none',
      fontSize: theme.typography.pxToRem(12),
      textAlign: 'left',
      width: '100%'
    },
    clearButtonDisabled: {
      color: theme.palette.text.disabled,
      cursor: 'not-allowed'
    },
    // Only applied when a secondary action button is also present, so the pair of buttons can
    // sit side by side instead of the single-button width: 100% full-row click target.
    clearButtonAuto: {
      width: 'auto'
    },
    secondaryActionButton: {
      textAlign: 'right'
    }
  }),
  { name: 'InlineColorPicker' }
)

export interface InlineColorPickerProps
  extends CoreColorPickerProps,
    Omit<MuiInputProps, 'onChange' | 'classes' | 'value' | 'label' | 'ref'> {
  swatchOnly?: boolean
  swatchIcon?: React.ReactNode
  labelAsValue?: boolean
  fullWidth?: boolean
  useResponsiveSwatchBorder?: boolean
  classes?: StyleClasses<typeof useStyles>
  onClose?: (hex?: string) => void
  onOpen?: () => void
  presetColors?: string[]
  useClearOption?: boolean
  /** Overrides the default "Clear" footer button text, e.g. "Reset" when a `defaultColor` (not
   * just empty/transparent) is the value it clears back to. */
  clearLabel?: string
  clearColor?: string
  defaultColor?: string
  onClear?: () => void
  /** Renders a second footer button (e.g. "Remove") alongside the "Clear"/`clearLabel` one, for
   * a distinct action such as fully removing a background rather than resetting it to default. */
  secondaryActionLabel?: string
  onSecondaryAction?: () => void
  secondaryActionDisabled?: boolean
  hideSelectionHighlight?: boolean
  /** Forwarded to the popover — pass when the default `document.body` portal target would escape
   * an ancestor that outside-click/focus logic treats as the "inside" boundary (e.g. PaintScout's
   * PresentationInspector, which checks `#presentation-inspector` via `isEditorChromeClick`). */
  container?: React.ComponentProps<typeof Popover>['container']
}

function InlineColorPicker(props: InlineColorPickerProps) {
  const {
    onChange,
    value,
    swatchOnly = false,
    swatchIcon,
    labelAsValue,
    label,
    disabled,
    useResponsiveSwatchBorder,
    onClose,
    onOpen,
    presetColors,
    useClearOption,
    clearLabel,
    clearColor,
    defaultColor,
    onClear,
    secondaryActionLabel,
    onSecondaryAction,
    secondaryActionDisabled,
    hideSelectionHighlight = false,
    container,
    ...otherProps
  } = props
  const theme = useTheme()
  const classes = useStyles(props)
  const sketchStyles = React.useMemo(
    () => ({
      default: {
        picker: {
          borderRadius: 0,
          boxShadow: 'none'
        },
        saturation: {
          position: 'relative'
        },
        Saturation: {
          radius: '12px',
          pointer: {
            width: 14,
            height: 14,
            borderRadius: '50%',
            transform: 'translate(-7px, -7px)',
            border: '1px solid #fff',
            boxShadow: '0 0 0 1px rgba(0,0,0,0.25)'
          },
          circle: {
            display: 'none'
          }
        },
        hue: {
          height: theme.typography.pxToRem(4),
          borderRadius: theme.typography.pxToRem(2)
        },
        Hue: {
          radius: theme.typography.pxToRem(2),
          slider: {
            width: theme.typography.pxToRem(18),
            height: theme.typography.pxToRem(6),
            borderRadius: theme.typography.pxToRem(1.5),
            background: '#fff',
            boxShadow: 'none',
            transform: 'translateX(calc(-9px))'
          }
        }
      }
    }),
    [theme]
  )
  const [color, setColor] = useState(value)
  const [anchorPosition, setAnchorPosition] = useState({ top: 0, left: 0 })
  const [open, setOpen] = useState(false)
  const [hasTypedHexChanges, setHasTypedHexChanges] = useState(false)
  const popoverContentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setColor(value)
  }, [value])

  const handleClose = () => {
    if (hasTypedHexChanges && onChange && isCompleteHex(color) && color !== value) {
      onChange(color)
    }
    setHasTypedHexChanges(false)
    setOpen(false)
    if (onClose) {
      onClose(color)
    }
  }
  const normalizedDefaultColor = defaultColor ?? ''
  const currentColor = color ?? ''
  const canClear = useClearOption && Boolean(currentColor && currentColor !== normalizedDefaultColor)

  useEffect(() => {
    if (!hideSelectionHighlight || !open || typeof document === 'undefined') {
      return
    }
    const styleElement = document.createElement('style')
    styleElement.id = SELECTION_STYLE_ID
    styleElement.innerHTML = `
      .ProseMirror ::selection,
      .ProseMirror *::selection {
        background-color: transparent !important;
      }
    `
    document.head.appendChild(styleElement)
    return () => {
      styleElement.remove()
    }
  }, [hideSelectionHighlight, open])
  return (
    <div className={classes.root}>
      {swatchIcon && (
        <div
          onMouseDownCapture={(ev) => {
            ev.preventDefault()
          }}
          onMouseDown={(ev) => {
            ev.preventDefault()
          }}
          onClick={handleSwatchClick}
        >
          {swatchIcon}
        </div>
      )}
      {!swatchIcon && (
        <>
          {!swatchOnly && (
            <InputField
              {...(otherProps as any)}
              value={labelAsValue ? label : currentColor}
              disabled={disabled || labelAsValue ? true : false}
              label={labelAsValue ? '' : label}
              classes={{ root: classes.input, adornment: classes.inputAdornment }}
              onChange={handleInputChange}
              endAdornment={
                <Swatch
                  useResponsiveSwatchBorder={useResponsiveSwatchBorder}
                  className={classes.swatch}
                  hex={currentColor}
                  onClick={handleSwatchClick}
                />
              }
            />
          )}
          {swatchOnly && (
            <Swatch
              useResponsiveSwatchBorder={useResponsiveSwatchBorder}
              className={classes.swatch}
              hex={currentColor}
              onClick={handleSwatchClick}
            />
          )}
        </>
      )}

      <Popover
        open={open}
        container={container}
        anchorPosition={anchorPosition}
        anchorReference={'anchorPosition'}
        onClose={handleClose}
        PaperProps={{ className: classes.popoverPaper }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
      >
        <div
          ref={popoverContentRef}
          className={classes.popoverContent}
          onMouseDown={handlePopoverMouseDown}
          onFocus={handleSketchFieldFocus}
        >
          <StyledSketchPicker
            styles={sketchStyles}
            color={color}
            onChange={({ hex }) => {
              setColor(hex)
              if (isEditingPickerInput()) {
                if (!shouldPropagatePickerInputChange()) {
                  setHasTypedHexChanges(true)
                  return
                }
                setHasTypedHexChanges(false)
              }
              if (onChange && isCompleteHex(hex)) onChange(hex)
            }}
            disableAlpha={true}
            presetColors={presetColors}
          />
          {(useClearOption || onSecondaryAction) && (
            <div className={classes.clearFooter}>
              {useClearOption && (
                <button
                  type="button"
                  className={classNames(classes.clearButton, {
                    [classes.clearButtonDisabled]: !canClear,
                    [classes.clearButtonAuto]: Boolean(onSecondaryAction)
                  })}
                  onClick={() => canClear && handleClear()}
                  disabled={!canClear}
                >
                  {clearLabel ?? 'Clear'}
                </button>
              )}
              {onSecondaryAction && (
                <button
                  type="button"
                  className={classNames(classes.clearButton, classes.clearButtonAuto, classes.secondaryActionButton, {
                    [classes.clearButtonDisabled]: secondaryActionDisabled
                  })}
                  onClick={onSecondaryAction}
                  disabled={secondaryActionDisabled}
                >
                  {secondaryActionLabel ?? 'Remove'}
                </button>
              )}
            </div>
          )}
        </div>
      </Popover>
    </div>
  )

  function handleClear() {
    const fallback = clearColor ?? defaultColor ?? ''
    setColor(fallback)
    if (fallback && onChange) {
      onChange(fallback)
    }
    if (onClear) {
      onClear()
    }
  }

  function handleSwatchClick(ev: any) {
    const rect = ev.currentTarget.parentElement.parentElement.getBoundingClientRect()
    const { bottom, right } = rect
    if (!disabled) {
      if (onOpen) onOpen()
      setOpen(true)
      setAnchorPosition({ top: bottom, left: right })
    }
  }

  function handleInputChange(ev: React.ChangeEvent<HTMLInputElement>) {
    const hex = ev.target.value
    setColor(hex)
    setHasTypedHexChanges(true)
    if (onChange && isCompleteHex(hex)) {
      onChange(hex)
    }
  }

  function handlePopoverMouseDown(ev: React.MouseEvent<HTMLElement>) {
    const target = ev.target as HTMLElement
    if (target.closest('.sketch-picker, input, textarea, button, [contenteditable="true"]')) {
      return
    }
    ev.preventDefault()
  }

  // react-color's fields pre-fill with the current value and don't select it on focus, so typing
  // over an existing value inserts characters instead of replacing them, which can silently produce
  // an over-length string that react-color's own hex validation rejects without ever calling back.
  // Listens on the popover wrapper (rather than on react-color's inputs directly) because those
  // inputs are rendered by react-color, not by us, so there's nothing local to attach a handler to.
  function handleSketchFieldFocus(ev: React.FocusEvent<HTMLElement>) {
    const target = ev.target
    if (
      target.closest('.sketch-picker') &&
      (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)
    ) {
      target.select()
    }
  }

  function Swatch({ hex, useResponsiveSwatchBorder: _useResponsiveSwatchBorder, onClick }: any) {
    return (
      <div
        onMouseDownCapture={(ev) => {
          ev.preventDefault()
        }}
        onMouseDown={(ev) => {
          ev.preventDefault()
        }}
        onClick={onClick}
        className={classes.swatch}
        style={{ backgroundColor: hex ?? '#000', outline: '1px solid #7a7a7a' }}
      />
    )
  }

  function isCompleteHex(hex: string) {
    return /^#[0-9a-f]{6}$/i.test(hex.trim())
  }

  // Scoped to this instance's own popover subtree (via popoverContentRef) rather than a
  // page-global `document.activeElement` check, since every mounted SketchPicker shares the
  // same `.sketch-picker` class — a global check can read a different field's (possibly
  // stale, mid-keystroke) focused input when multiple palette pickers exist on the page.
  function isEditingPickerInput() {
    if (typeof document === 'undefined') {
      return false
    }
    const activeElement = document.activeElement as HTMLElement | null
    if (!activeElement) {
      return false
    }
    return Boolean(popoverContentRef.current?.contains(activeElement) && activeElement.matches('input, textarea'))
  }

  function shouldPropagatePickerInputChange() {
    if (typeof document === 'undefined') {
      return true
    }
    const activeElement = document.activeElement as HTMLInputElement | HTMLTextAreaElement | null
    // Only reached via the isEditingPickerInput() gate, which already requires a non-null,
    // in-subtree activeElement — the null case can't occur here today, but keeps the original
    // permissive default rather than silently changing behaviour if that gate is ever loosened.
    if (!activeElement) {
      return true
    }
    // A focused element from a different picker's popover (stale or foreign) must never drive
    // this instance's commit decision.
    if (!popoverContentRef.current?.contains(activeElement)) {
      return false
    }
    const rawValue = activeElement.value?.trim() ?? ''
    if (!rawValue) {
      return false
    }

    // RGB number fields should propagate immediately for live preview updates.
    if (/^-?\d+$/.test(rawValue)) {
      return true
    }

    // Hex field can be typed incrementally; only propagate once a full 6-digit value is entered.
    if (/^#?[0-9a-f]+$/i.test(rawValue)) {
      const normalizedHex = rawValue.startsWith('#') ? rawValue.slice(1) : rawValue
      return normalizedHex.length >= 6
    }
    return false
  }
}

export default InlineColorPicker
